import { ComponentPropsWithoutRef, MouseEventHandler, ReactNode } from "react";
import classNames from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconClose from "app/pages/.shared/IconClose";
import "./Tag.scss";

export const TAG_SIZES = {
	LARGE: "large",
	MEDIUM: "medium",
	SMALL: "small",
} as const;

export const TAG_ICON_ALIGNMENTS = {
	LEFT: "left",
	RIGHT: "right",
} as const;

export type TagSize = (typeof TAG_SIZES)[keyof typeof TAG_SIZES];
export type TagIconAlignment = (typeof TAG_ICON_ALIGNMENTS)[keyof typeof TAG_ICON_ALIGNMENTS];
type TagTypographyVariant = (typeof TYPOGRAPHY_VARIANTS)[keyof typeof TYPOGRAPHY_VARIANTS];

const SIZE_TO_TYPOGRAPHY_VARIANT_DEFAULT = {
	[TAG_SIZES.LARGE]: TYPOGRAPHY_VARIANTS.REGULAR,
	[TAG_SIZES.MEDIUM]: TYPOGRAPHY_VARIANTS.SMALL,
	[TAG_SIZES.SMALL]: TYPOGRAPHY_VARIANTS.SMALL,
} as const;

type TagProps = {
	children: ReactNode;
	icon?: ReactNode;
	alignIcon?: TagIconAlignment;
	textVariant?: TagTypographyVariant;
	size?: TagSize;
	isBold?: boolean;
	showRemoveIcon?: boolean;
	onClick?: MouseEventHandler<HTMLSpanElement>;
	className?: string;
} & Omit<ComponentPropsWithoutRef<"span">, "className" | "children" | "onClick">;

const Tag = ({
	children,
	textVariant,
	size = TAG_SIZES.MEDIUM,
	isBold = false,
	showRemoveIcon = false,
	icon,
	alignIcon = TAG_ICON_ALIGNMENTS.LEFT,
	onClick,
	className,
	...rest
}: TagProps) => {
	const typoVariant = textVariant || SIZE_TO_TYPOGRAPHY_VARIANT_DEFAULT[size];
	const tagClassName = classNames(
		"tag",
		`tag--${size}`,
		{ "tag--clickable": Boolean(onClick) },
		className
	);

	return (
		<span className={tagClassName} data-testid="tag" onClick={onClick} {...rest}>
			{icon && alignIcon === TAG_ICON_ALIGNMENTS.LEFT && (
				<span className="tag__icon tag__icon--left">{icon}</span>
			)}
			<Typography
				component="span"
				variant={typoVariant}
				isBold={isBold}
				className="tag__label"
			>
				{children}
			</Typography>
			{icon && alignIcon === TAG_ICON_ALIGNMENTS.RIGHT && (
				<span className="tag__icon tag__icon--right">{icon}</span>
			)}
			{showRemoveIcon && (
				<span className="tag__remove-icon" data-testid="tag-remove-icon">
					<IconClose width={16} height={16} />
				</span>
			)}
		</span>
	);
};

export default Tag;
